import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Statistic, Divider, Tag } from 'antd';
import { 
  ArrowUpOutlined, 
  FallOutlined, 
  RiseOutlined 
} from '@ant-design/icons';
import { formatPriceToman } from '../../../utils/formatPriceToman';
import { financialApi } from '../../../utils/financialApi';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { extractApiUserMessage } from '../../../utils/api';

const { Text } = Typography;

const ProfitAndLoss = ({ startDate, endDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPnL = async () => {
      try {
        setLoading(true);
        // By default fetching current month
        const res = await financialApi.getProfitAndLoss(startDate, endDate);
        // Assuming API might return data directly or nested
        const pnlData = res?.pnl || res?.data?.pnl || res;
        setData(pnlData);
      } catch (error) {
        console.error('Failed to fetch P&L:', extractApiUserMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchPnL();
  }, [startDate, endDate]);

  if (!data && !loading) return null;

  const { revenue = {}, operating_costs = {}, profitability = {} } = data || {};

  const isProfitable = profitability.status === 'profitable';
  
  const costData = [
    { name: 'هزینه‌های عملیاتی', value: operating_costs.operating_expenses || 0, color: '#f59e0b' },
    { name: 'پورسانت فروش', value: operating_costs.sales_commissions || 0, color: '#3b82f6' },
    { name: 'هزینه حقوق و دستمزد', value: operating_costs.payroll_expenses || 0, color: '#ef4444' },
  ].filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div dir="rtl" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 rounded-lg shadow-lg">
          <p className="text-gray-700 dark:text-gray-300 mb-1">{payload[0].name}</p>
          <p className="font-bold text-gray-900 dark:text-gray-100">{formatPriceToman(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card 
      title="سود و زیان مدیریتی بلادرنگ (P&L)" 
      extra={
        data?.period?.jalali_month ? (
          <Tag color="blue" className="text-xs m-0">
            دوره شمسی: {data.period.jalali_month} ({data.period.jalali_start} تا {data.period.jalali_end})
          </Tag>
        ) : null
      }
      loading={loading}
      bordered={false} 
      className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12} lg={8}>
          <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700 h-full">
            <Text className="text-gray-500 dark:text-gray-400 block mb-2">خلاصه درآمدها</Text>
            <Statistic 
              title={<span className="text-xs">درآمد خالص</span>}
              value={revenue.net_revenue || 0} 
              formatter={val => formatPriceToman(val, { showCurrency: false })}
              suffix="تومان"
              valueStyle={{ color: '#10b981', fontWeight: 'bold' }} 
              prefix={<ArrowUpOutlined />}
            />
            <Divider className="my-3" />
            <div className="flex justify-between text-xs mb-2">
              <Text className="text-gray-500">فروش معاملات موفق (Won)</Text>
              <Text className="font-medium text-emerald-600">{formatPriceToman(revenue.invoiced_sales || 0)}</Text>
            </div>
            <div className="flex justify-between text-xs mb-2">
              <Text className="text-gray-500">وجوه نقد وصولی</Text>
              <Text className="font-medium text-blue-500">{formatPriceToman(revenue.collected_cash || 0)}</Text>
            </div>
            <div className="flex justify-between text-xs">
              <Text className="text-gray-500">عودت وجه</Text>
              <Text className="font-medium text-red-500">{formatPriceToman(revenue.refunds || 0)}</Text>
            </div>
          </div>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700 h-full">
            <Text className="text-gray-500 dark:text-gray-400 block mb-2">وضعیت سودآوری</Text>
            <Statistic 
              title={<span className="text-xs">سود عملیاتی خالص</span>}
              value={profitability.net_operating_profit || 0} 
              formatter={val => formatPriceToman(val, { showCurrency: false })}
              suffix="تومان"
              valueStyle={{ color: isProfitable ? '#10b981' : '#ef4444', fontWeight: 'bold' }} 
              prefix={isProfitable ? <RiseOutlined /> : <FallOutlined />}
            />
            <Divider className="my-3" />
            <div className="flex justify-between items-center text-xs mb-2">
              <Text className="text-gray-500">حاشیه سود عملیاتی</Text>
              <Tag color={isProfitable ? 'success' : 'error'} className="m-0 font-bold">
                ٪{profitability.operating_margin_percentage || 0}
              </Tag>
            </div>
            <div className="flex justify-between items-center text-xs">
              <Text className="text-gray-500">وضعیت</Text>
              <Text className={`font-bold ${isProfitable ? 'text-emerald-500' : 'text-red-500'}`}>
                {isProfitable ? 'سودده' : 'زیان‌ده'}
              </Text>
            </div>
          </div>
        </Col>

        <Col xs={24} md={24} lg={8}>
          <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700 h-full flex flex-col">
            <Text className="text-gray-500 dark:text-gray-400 block mb-2">ترکیب هزینه‌ها</Text>
            <div className="flex-1 min-h-[150px] relative">
              {costData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={costData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {costData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs">
                  داده‌ای برای نمایش وجود ندارد
                </div>
              )}
            </div>
            <div className="mt-2 text-center text-xs text-gray-500">
              کل هزینه‌ها: <span className="font-bold text-gray-700 dark:text-gray-300">{formatPriceToman(operating_costs.total_operating_costs || 0)}</span>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default ProfitAndLoss;
